import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/app/DashboardLayout'
import { ChildAvatar } from '@/components/app/ChildAvatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Question, Questionnaire, Child } from '@/types/dashboard.types'
import { AGE_RANGES, CATEGORY_CONFIG, CATEGORY_LABEL_FILIPINO } from '@/constants/dashboard.constants'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { AuthAPI, ChildAPI, QuestionnaireAPI, AssessmentAPI } from '@/services/api'
import { Loader2, Check, Languages } from 'lucide-react'
import { toast } from 'sonner'

const STORAGE_KEY_PREFIX = 'tinytalk-assessment'

const RATING_GUIDE_FIL =
  '1 = Hindi pa nagagawa, 2 = Bihira, 3 = Minsan, 4 = Madalas, 5 = Palagi/consistent.'
const RATING_GUIDE_EN =
  '1 = Never / not yet, 2 = Rarely, 3 = Sometimes, 4 = Often, 5 = Always / consistent.'

/** Persists for the whole questionnaire across questions, refresh, and return visits (same browser). */
const SHOW_ENGLISH_STORAGE_KEY = 'tinytalk-assessment-prefer-english'
const SHOW_ENGLISH_LEGACY_SESSION_KEY = 'tinytalk-assessment-show-english'

const readShowEnglishPreference = (): boolean => {
  try {
    const stored = localStorage.getItem(SHOW_ENGLISH_STORAGE_KEY)
    if (stored === '1') return true
    if (stored === '0') return false
    const legacy = sessionStorage.getItem(SHOW_ENGLISH_LEGACY_SESSION_KEY)
    if (legacy === '1' || legacy === '0') {
      localStorage.setItem(SHOW_ENGLISH_STORAGE_KEY, legacy)
      return legacy === '1'
    }
    return false
  } catch {
    return false
  }
}

/** Speech & language screening scope — matches questionnaire + `ML_CHILD_AGE_*` (backend) and engine `ML_AGE_*_MONTHS`. */
const isOutOfScreeningAgeMonths = (totalMonths: number) =>
  totalMonths < AGE_RANGES.MIN_MONTHS || totalMonths > AGE_RANGES.MAX_MONTHS

const SCREENING_AGE_SCOPE_FIL =
  'Ang TinyTalk screening ay para sa edad 2–5 taon (24–60 buwan) — speech at language development. Wala pang questionnaire para sa labas ng saklaw na ito.'

interface AssessmentFlowProps {
  childId?: string
}

const getAgeFromDateOfBirth = (dateOfBirth: string) => {
  const birthDate = new Date(dateOfBirth)
  const today = new Date()

  let years = today.getFullYear() - birthDate.getFullYear()
  let months = today.getMonth() - birthDate.getMonth()

  if (today.getDate() < birthDate.getDate()) {
    months -= 1
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  return {
    years,
    months,
    totalMonths: years * 12 + months,
  }
}

const getAgePartsFromMonths = (totalMonths: number) => {
  const safeMonths = Math.max(0, totalMonths)
  return {
    years: Math.floor(safeMonths / 12),
    months: safeMonths % 12,
    totalMonths: safeMonths,
  }
}

const getChildAgeParts = (child: Child) => {
  if (child.ageInMonths !== undefined) {
    return getAgePartsFromMonths(child.ageInMonths)
  }

  return getAgeFromDateOfBirth(child.dateOfBirth)
}


const getStorageKey = (childId: string): string => `${STORAGE_KEY_PREFIX}-${childId}`

const loadProgress = (childId: string): { index: number; answers: Record<string, string> } => {
  try {
    const raw = localStorage.getItem(getStorageKey(childId))
    if (!raw) return { index: 0, answers: {} }
    const parsed = JSON.parse(raw) as { index?: number; answers?: Record<string, string> }
    return {
      index: typeof parsed.index === 'number' ? Math.min(parsed.index, 39) : 0,
      answers: parsed.answers && typeof parsed.answers === 'object' ? parsed.answers : {},
    }
  } catch {
    return { index: 0, answers: {} }
  }
}

const saveProgress = (childId: string, index: number, answers: Record<string, string>): void => {
  try {
    const safeIndex = Math.max(0, index)
    localStorage.setItem(getStorageKey(childId), JSON.stringify({ index: safeIndex, answers }))
  } catch {
    // ignore
  }
}

export const AssessmentFlow = ({ childId: propChildId }: AssessmentFlowProps = {}) => {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { childId?: string }
  const search = useSearch({ strict: false }) as { resume?: string }
  const urlChildId = params?.childId || propChildId

  const [user, setUser] = useState<any>(null)
  const [child, setChild] = useState<Child | null>(null)
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingResponse, setIsSavingResponse] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [assessmentId, setAssessmentId] = useState<string | null>(null)
  const [questionnaireMeta, setQuestionnaireMeta] = useState<{
    fitQuestionsCount?: number
    ageRange?: { minMonths?: number; maxMonths?: number; minYears?: number; maxYears?: number }
    childAge?: { months?: number; years?: number; ageBand?: string; ageYearsFloor?: number }
  } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [childrenOptions, setChildrenOptions] = useState<Child[]>([])
  const [selectedStartChildId, setSelectedStartChildId] = useState<string>('')
  const [selectedStartChild, setSelectedStartChild] = useState<Child | null>(null)
  const [startQuestionnairePreview, setStartQuestionnairePreview] = useState<Questionnaire | null>(null)
  const [startPreviewMeta, setStartPreviewMeta] = useState<{
    fitQuestionsCount?: number
    ageRange?: { minMonths?: number; maxMonths?: number; minYears?: number; maxYears?: number }
    childAge?: { months?: number; years?: number; ageBand?: string; ageYearsFloor?: number }
  } | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [showEnglish, setShowEnglish] = useState(readShowEnglishPreference)

  const toggleShowEnglish = () => {
    setShowEnglish((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SHOW_ENGLISH_STORAGE_KEY, next ? '1' : '0')
        sessionStorage.removeItem(SHOW_ENGLISH_LEGACY_SESSION_KEY)
      } catch {
        // ignore
      }
      return next
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const u = await AuthAPI.me()
        setUser(u.user)
        setLoadError(null)

        const children = await ChildAPI.getChildren()
        const activeChildren = (children || []).filter((item: Child) => item.isActive !== false)
        setChildrenOptions(activeChildren)

        if (!urlChildId) {
          if (activeChildren.length > 0) {
            const firstChildId = activeChildren[0].id
            setSelectedStartChildId(firstChildId)
          }
          setIsHydrated(true)
          return
        }

        let selectedChild: Child | null = null
        if (urlChildId) {
          const c = await ChildAPI.getChild(urlChildId)
          selectedChild = c
          setChild(c)

          const childAge = getChildAgeParts(c)
          if (isOutOfScreeningAgeMonths(childAge.totalMonths)) {
            setQuestionnaire(null)
            setQuestions([])
            setLoadError(SCREENING_AGE_SCOPE_FIL)
            return
          }
        }

        const qData = await QuestionnaireAPI.getActiveQuestionnaire(selectedChild?.id)
        setQuestionnaire(qData)
        setQuestionnaireMeta(qData.summary || null)

        let allQuestions: Question[] = []
        if (qData.questionsByDomain) {
          Object.values(qData.questionsByDomain).forEach((domainQuestions: any) => {
            allQuestions = [...allQuestions, ...domainQuestions]
          })
        }
        const sortedQuestions = allQuestions.sort((a, b) => a.order - b.order)
        setQuestions(sortedQuestions)

        if (sortedQuestions.length === 0) {
          const childAgeYears = qData?.summary?.childAge?.ageYearsFloor
          setLoadError(
            childAgeYears
              ? `Walang naka-tag na tanong para sa age ${childAgeYears}. Paki-set ang age visibility sa admin questionnaire.`
              : 'Walang age-matched questions available for this child. Please review questionnaire age-group settings.'
          )
        }

        if (urlChildId) {
          // start new assessment and get ID
          try {
            const active = await AssessmentAPI.startAssessment(urlChildId)
            setAssessmentId(active.assessmentId || active.id)
          } catch (aErr) {
            console.error("Unable to start assessment or perhaps continuing", aErr)
            // could perhaps also check if we have a continuing one
          }
        }
      } catch (e) {
        console.error("Failed to init AssessmentFlow", e)
        setLoadError('Walang available na questionnaire para sa edad ng bata. Paki-check ang child profile at age range settings.')
      } finally {
        setIsLoadingData(false)
      }
    }
    fetchData()
  }, [urlChildId])

  useEffect(() => {
    const fetchStartPreview = async () => {
      if (urlChildId || !selectedStartChildId) return

      try {
        setIsLoadingPreview(true)
        const selected = childrenOptions.find((item) => item.id === selectedStartChildId) || null
        setSelectedStartChild(selected)

        if (selected) {
          const selectedAge = getChildAgeParts(selected)
          if (isOutOfScreeningAgeMonths(selectedAge.totalMonths)) {
            setStartQuestionnairePreview(null)
            setStartPreviewMeta(null)
            setLoadError(SCREENING_AGE_SCOPE_FIL)
            return
          }
        }

        const qData = await QuestionnaireAPI.getActiveQuestionnaire(selectedStartChildId)
        setStartQuestionnairePreview(qData)
        setStartPreviewMeta(qData.summary || null)
        setLoadError(null)
      } catch (error) {
        console.error('Failed to load questionnaire preview for selected child', error)
        setStartQuestionnairePreview(null)
        setStartPreviewMeta(null)
        setLoadError('Walang available na questionnaire para sa napiling bata. Paki-check ang age range settings.')
      } finally {
        setIsLoadingPreview(false)
      }
    }

    fetchStartPreview()
  }, [selectedStartChildId, childrenOptions, urlChildId])

  useEffect(() => {
    if (!urlChildId || questions.length === 0) return
    const { index, answers: savedAnswers } = loadProgress(urlChildId)
    const resumeFromReport = search?.resume === 'true'
    const maxIndex = questions.length - 1
    if (resumeFromReport && index < maxIndex) {
      setCurrentQuestionIndex(Math.min(index + 1, maxIndex))
      setAnswers(savedAnswers)
    } else {
      setCurrentQuestionIndex(Math.min(index, maxIndex))
      setAnswers(savedAnswers)
    }
    setIsHydrated(true)
  }, [urlChildId, questions.length])

  useEffect(() => {
    if (!isHydrated || !urlChildId) return
    saveProgress(urlChildId, currentQuestionIndex, answers)
  }, [urlChildId, currentQuestionIndex, answers, isHydrated])

  if (isLoadingData) {
    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    )
  }

  if (!urlChildId) {
    const selectedStartChildAge = selectedStartChild ? getChildAgeParts(selectedStartChild) : null
    const isSelectedChildAgeUnsupported =
      selectedStartChildAge ? isOutOfScreeningAgeMonths(selectedStartChildAge.totalMonths) : false

    return (
      <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
        <div className="max-w-2xl mx-auto py-6">
          <Card className="border-transparent bg-card shadow-sm">
            <CardHeader>
              <CardTitle>Start Assessment</CardTitle>
              <CardDescription>
                Piliin kung sinong child ang sasagot. Speech at language screening para sa{' '}
                <span className="font-medium">2–5 taon (24–60 buwan)</span>; age-based ang tanong na lalabas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {childrenOptions.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Wala ka pang active child profile. Mag-add muna ng child bago mag-start ng assessment.
                  </p>
                  <Button onClick={() => navigate({ to: '/dashboard/children' })}>Go to Children Management</Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="child-selector">Select Child</Label>
                    <Select value={selectedStartChildId} onValueChange={setSelectedStartChildId}>
                      <SelectTrigger id="child-selector">
                        <SelectValue placeholder="Choose a child profile" />
                      </SelectTrigger>
                      <SelectContent>
                        {childrenOptions.map((option) => {
                          const optionAge = option.ageInMonths !== undefined
                            ? getAgePartsFromMonths(option.ageInMonths)
                            : getAgeFromDateOfBirth(option.dateOfBirth)
                          return (
                            <SelectItem key={option.id} value={option.id}>
                              {option.firstName} {option.lastName} ({optionAge.years}y {optionAge.months}m)
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedStartChild && (
                    <div className="rounded-lg border border-border bg-background p-4 space-y-2">
                      <p className="text-sm font-medium">
                        Selected: {selectedStartChild.firstName} {selectedStartChild.lastName}
                      </p>
                      {(() => {
                        const previewAge = startPreviewMeta?.childAge?.months !== undefined
                          ? getAgePartsFromMonths(startPreviewMeta.childAge.months)
                          : getChildAgeParts(selectedStartChild)
                        return (
                          <p className="text-sm text-muted-foreground">
                            Age: {previewAge.years} years {previewAge.months} months
                          </p>
                        )
                      })()}
                      {isSelectedChildAgeUnsupported && (
                        <p className="text-sm text-destructive">{SCREENING_AGE_SCOPE_FIL}</p>
                      )}
                      {isLoadingPreview ? (
                        <p className="text-sm text-muted-foreground">Loading questionnaire match...</p>
                      ) : startQuestionnairePreview ? (
                        <p className="text-sm text-muted-foreground">
                          Matched questionnaire: {startQuestionnairePreview.title}
                          {startPreviewMeta?.fitQuestionsCount !== undefined
                            ? ` (${startPreviewMeta.fitQuestionsCount} age-fit questions)`
                            : ''}
                        </p>
                      ) : (
                        <p className="text-sm text-destructive">No matching questionnaire found for this child.</p>
                      )}

                      {(startPreviewMeta?.fitQuestionsCount ?? 0) === 0 && !isLoadingPreview && (
                        <p className="text-sm text-destructive">
                          {startPreviewMeta?.childAge?.ageYearsFloor
                            ? `Walang naka-tag na tanong para sa age ${startPreviewMeta.childAge.ageYearsFloor}. Paki-set ang age visibility sa admin questionnaire.`
                            : 'Walang age-matched questions para sa child na ito. Paki-check age groups ng active questionnaire.'}
                        </p>
                      )}
                    </div>
                  )}

                  {loadError && (
                    <p className="text-sm text-destructive">{loadError}</p>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={() => navigate({ to: `/dashboard/assessment/${selectedStartChildId}` })}
                      disabled={!selectedStartChildId || !startQuestionnairePreview || isLoadingPreview || (startPreviewMeta?.fitQuestionsCount ?? 0) === 0 || isSelectedChildAgeUnsupported}
                    >
                      Start Assessment
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  // Keep showing loading while route data is still settling; only show error for explicit failures.
  if (!child || !questionnaire || questions.length === 0) {
    if (!loadError) {
      return (
        <DashboardLayout user={user || { firstName: 'User', lastName: '', email: '', role: 'PARENT' }}>
          <div className="flex h-[50vh] items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p>Loading matched questionnaire...</p>
          </div>
        </DashboardLayout>
      )
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-muted-foreground">Unable to start assessment</h2>
          <p className="text-sm text-muted-foreground mt-2">{loadError}</p>
        </div>
      </div>
    )
  }

  if (!isHydrated) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[50vh]">
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </DashboardLayout>
    )
  }

  const currentQuestion = questions[currentQuestionIndex]
  const totalQuestions = questions.length
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0
  const isAnswered = currentQuestion ? (answers[currentQuestion.id] || '').trim().length > 0 : false
  const canProceed = currentQuestion ? isAnswered : false
  const hasAssessmentSession = Boolean(assessmentId)

  const notifySessionNotReady = () => {
    toast.error(
      showEnglish
        ? 'Assessment session is not ready yet. Please wait a moment and try again.'
        : 'Hindi pa handa ang assessment session. Sandali lang at subukan ulit.'
    )
  }

  const persistResponse = async (questionId: string, answer: string): Promise<boolean> => {
    if (!assessmentId) return true

    try {
      setIsSavingResponse(true)
      await AssessmentAPI.saveResponse(assessmentId, questionId, answer)
      return true
    } catch (err) {
      console.error('Failed to save response', err)
      toast.error('Hindi na-save ang sagot. Paki-try ulit bago mag-proceed.')
      return false
    } finally {
      setIsSavingResponse(false)
    }
  }

  const persistCurrentQuestionResponse = async (): Promise<boolean> => {
    if (!currentQuestion) return false
    const currentAnswer = answers[currentQuestion.id] || ''
    if (currentAnswer.trim().length === 0) {
      toast.error(
        showEnglish
          ? 'Please answer this question before proceeding.'
          : 'Kailangan sagutan muna ang tanong na ito bago mag-proceed.'
      )
      return false
    }

    return persistResponse(currentQuestion.id, currentAnswer)
  }

  const syncAllAnswersBeforeSubmit = async (): Promise<boolean> => {
    if (!assessmentId) return true

    const answeredQuestions = questions.filter((question) => {
      const value = answers[question.id] || ''
      return value.trim().length > 0
    })

    for (const question of answeredQuestions) {
      const ok = await persistResponse(question.id, answers[question.id])
      if (!ok) {
        return false
      }
    }

    return true
  }

  const handleAnswerChange = (answer: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: answer }))
  }

  const handleNextQuestion = async () => {
    if (!isAnswered) {
      toast.error(
        showEnglish
          ? 'Please select or enter an answer before going to the next question.'
          : 'Pumili o maglagay muna ng sagot bago pumunta sa susunod na tanong.'
      )
      return
    }

    if (!hasAssessmentSession) {
      notifySessionNotReady()
      return
    }

    const saved = await persistCurrentQuestionResponse()
    if (!saved) return

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1)
    }
  }

  const handlePreviousQuestion = async () => {
    // Always attempt to save any existing answer before going back.
    // Navigation is NOT blocked on save failure — syncAllAnswersBeforeSubmit()
    // is the final safety net that re-saves everything before submission.
    const currentAnswer = currentQuestion ? answers[currentQuestion.id] || '' : ''
    if (currentQuestion && currentAnswer.trim().length > 0) {
      const saved = await persistCurrentQuestionResponse()
      if (!saved) {
        toast.error('Hindi na-save ang sagot sa server. Maa-update pa rin bago mag-submit.')
      }
    }

    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1)
    }
  }

  const isLastQuestion = currentQuestionIndex === questions.length - 1

  const handleSubmitAssessment = async (isPartial = false) => {
    if (!hasAssessmentSession) {
      notifySessionNotReady()
      return
    }

    setIsSubmitting(true)

    try {
      const firstUnansweredIndex = questions.findIndex((question) => {
        const value = answers[question.id] || ''
        return value.trim().length === 0
      })

      if (firstUnansweredIndex !== -1) {
        setCurrentQuestionIndex(firstUnansweredIndex)
        toast.error(
          showEnglish
            ? 'Please answer all questions before submitting.'
            : 'Kailangan masagutan muna lahat ng tanong bago mag-submit.'
        )
        return
      }

      const currentSaved = await persistCurrentQuestionResponse()
      if (!currentSaved) {
        return
      }

      const synced = await syncAllAnswersBeforeSubmit()
      if (!synced) {
        return
      }

      if (assessmentId) {
        await AssessmentAPI.submitAssessment(assessmentId, { isPartial })
      }

      // Clear saved progress only for full submit.
      if (!isPartial && urlChildId) {
        localStorage.removeItem(getStorageKey(urlChildId))
      }

      toast.success(
        isPartial
          ? 'Early analysis complete. Showing current result based on answered questions.'
          : 'Assessment submitted successfully.'
      )

      navigate({ to: `/dashboard/reports/${assessmentId}` })
    } catch (error: any) {
      console.error("Submit Assessment error", error)
      toast.error(error?.response?.data?.message || 'Failed to submit assessment')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!currentQuestion) {
    return (
      <DashboardLayout user={user}>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <p className="text-muted-foreground">No question available at this step.</p>
          <Button
            variant="outline"
            onClick={() => setCurrentQuestionIndex(0)}
            aria-label="Go back to first question"
          >
            Go to First Question
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  const categoryConfig = CATEGORY_CONFIG[currentQuestion.category]
  const categoryLabel = showEnglish
    ? categoryConfig.label
    : CATEGORY_LABEL_FILIPINO[currentQuestion.category]
  const CategoryIcon = categoryConfig.Icon
  const childAge = questionnaireMeta?.childAge?.months !== undefined
    ? getAgePartsFromMonths(questionnaireMeta.childAge.months)
    : getAgeFromDateOfBirth(child.dateOfBirth)

  return (
    <DashboardLayout user={user}>
      <div className="min-h-[calc(100vh-8rem)] bg-gradient-to-b from-teal-50/30 via-slate-50/20 to-blue-50/30 dark:bg-background dark:bg-none">
        <div className="mx-auto max-w-4xl space-y-6 px-0 py-4 sm:px-2 sm:py-6 animate-in fade-in duration-500">
          <div className="flex flex-col items-end gap-1 px-1">
            <Button
              type="button"
              variant={showEnglish ? 'default' : 'outline'}
              size="sm"
              onClick={toggleShowEnglish}
              className="gap-2"
              aria-pressed={showEnglish}
            >
              <Languages className="h-4 w-4 shrink-0" aria-hidden />
              {showEnglish ? 'Show Filipino' : 'Show English'}
            </Button>
            <p className="max-w-sm text-right text-xs text-muted-foreground">
              {showEnglish
                ? 'Language applies to every question in this questionnaire until you switch it.'
                : 'Ang wikang ito ay para sa lahat ng tanong sa questionnaire hanggang papalitan mo.'}
            </p>
          </div>
          {/* Header */}
          <Card className="border-transparent bg-gradient-to-br from-teal-100 via-teal-50 to-cyan-50 shadow-md animate-in slide-in-from-top duration-700 dark:border dark:border-border dark:bg-gradient-to-br dark:from-card dark:via-card dark:to-muted/40 dark:shadow-none">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <ChildAvatar gender={child.gender} size="lg" />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shadow-sm">
                    {currentQuestionIndex + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-xl md:text-2xl">
                    {showEnglish ? 'Assessment for' : 'Assessment para kay'}{' '}
                    {child.firstName} {child.lastName}
                  </CardTitle>
                  <CardDescription className="text-sm md:text-base mt-1">
                    {questionnaire.title}
                  </CardDescription>
                  <div className="text-xs md:text-sm text-muted-foreground mt-1 space-y-0.5">
                    <p>
                      {showEnglish ? 'Child age:' : 'Edad ng bata:'}{' '}
                      {childAge.years} {showEnglish ? 'years' : 'taon'}{' '}
                      {childAge.months} {showEnglish ? 'months' : 'buwan'}{' '}
                      ({childAge.totalMonths} {showEnglish ? 'months' : 'buwan'})
                    </p>
                    <p>
                      {showEnglish
                        ? 'Age filter: Locked from child profile (not editable during assessment)'
                        : 'Naka-lock ang filter ng edad mula sa profile ng bata (hindi mababago habang assessment).'}
                    </p>
                    {questionnaireMeta?.ageRange?.minMonths !== undefined && questionnaireMeta?.ageRange?.maxMonths !== undefined && (
                      <p>
                        {showEnglish ? 'Questionnaire range:' : 'Saklaw ng questionnaire:'}{' '}
                        {questionnaireMeta.ageRange.minMonths}-{questionnaireMeta.ageRange.maxMonths}{' '}
                        {showEnglish ? 'months' : 'buwan'}
                        {questionnaireMeta.ageRange.minYears !== undefined && questionnaireMeta.ageRange.maxYears !== undefined
                          ? ` (${questionnaireMeta.ageRange.minYears}-${questionnaireMeta.ageRange.maxYears} ${showEnglish ? 'years' : 'taon'})`
                          : ''}
                      </p>
                    )}
                    {questionnaireMeta?.childAge?.ageBand && (
                      <p>
                        {showEnglish ? 'Matched age group:' : 'Angkop na age group:'}{' '}
                        {questionnaireMeta.childAge.ageBand}
                      </p>
                    )}
                    <p>
                      {showEnglish ? 'Age-fit questions:' : 'Bilang ng angkop na tanong:'}{' '}
                      {questionnaireMeta?.fitQuestionsCount ?? totalQuestions}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: categoryConfig.color + '25',
                        color: categoryConfig.color,
                      }}
                    >
                      <CategoryIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {categoryLabel}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {showEnglish
                        ? `Question ${currentQuestionIndex + 1} of ${totalQuestions}`
                        : `Tanong ${currentQuestionIndex + 1} ng ${totalQuestions}`}
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground font-medium">
                    {showEnglish ? 'Progress' : 'Progreso'}
                  </span>
                  <span className="font-semibold text-primary">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>
            </CardContent>
          </Card>

          {/* Question Card */}
          <Card className="border-transparent bg-white/90 shadow-lg backdrop-blur-sm animate-in slide-in-from-bottom duration-700 overflow-hidden dark:border dark:border-border dark:bg-card/95 dark:backdrop-blur-none">
            <div
              className="h-1.5"
              style={{ backgroundColor: categoryConfig.color }}
              aria-hidden
            />
            <CardHeader className="pb-4">
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-sm"
                  style={{ backgroundColor: categoryConfig.color + '20' }}
                >
                  <CategoryIcon
                    className="h-7 w-7"
                    style={{ color: categoryConfig.color }}
                    aria-hidden
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-md"
                      style={{
                        backgroundColor: categoryConfig.color + '20',
                        color: categoryConfig.color,
                      }}
                    >
                      {categoryLabel}
                    </span>
                    {currentQuestion.isRequired && (
                      <span className="text-xs font-medium text-rose-600 bg-rose-50 px-2.5 py-1 rounded-md">
                        {showEnglish ? '* Required' : '* Kinakailangan'}
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-lg md:text-xl leading-snug">
                    {showEnglish && currentQuestion.questionTextEnglish
                      ? currentQuestion.questionTextEnglish
                      : currentQuestion.questionText}
                  </CardTitle>
                  {showEnglish && currentQuestion.questionTextEnglish ? (
                    <CardDescription className="text-base mt-3 text-muted-foreground/90 italic">
                      {currentQuestion.questionText}
                    </CardDescription>
                  ) : (
                    currentQuestion.questionTextEnglish && (
                      <CardDescription className="text-base mt-3 text-muted-foreground/90 italic">
                        {currentQuestion.questionTextEnglish}
                      </CardDescription>
                    )
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="rounded-lg border border-primary/20 bg-primary/10 p-3 text-sm text-primary/90">
                  <p className="font-semibold">{showEnglish ? 'Rating guide' : 'Gabay sa rating'}</p>
                  <p className="mt-1">{showEnglish ? RATING_GUIDE_EN : RATING_GUIDE_FIL}</p>
                </div>

                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={handleAnswerChange}
                  className="grid grid-cols-2 gap-3 sm:grid-cols-5"
                >
                  {[1, 2, 3, 4, 5].map((value) => {
                    const valueText = String(value)
                    const isSelected = answers[currentQuestion.id] === valueText
                    return (
                      <Label
                        key={value}
                        htmlFor={`rating-${value}`}
                        className={`flex cursor-pointer items-center justify-center space-x-2 rounded-xl border-2 p-4 transition-all duration-200 ${isSelected
                          ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                          : 'border-border bg-muted/50 hover:border-primary/40 hover:bg-primary/10 dark:bg-muted/30'
                          }`}
                      >
                        <RadioGroupItem value={valueText} id={`rating-${value}`} />
                        <span className="text-base font-semibold">{valueText}</span>
                      </Label>
                    )
                  })}
                </RadioGroup>
              </div>

              {/* Navigation — full-width buttons on small screens */}
              <div className="flex flex-col items-stretch gap-4 border-t border-border pt-8 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  variant="outline"
                  onClick={() => void handlePreviousQuestion()}
                  disabled={currentQuestionIndex === 0 || isSubmitting}
                  className="w-full gap-2 border-transparent bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 sm:w-auto"
                  aria-label={showEnglish ? 'Go to previous question' : 'Bumalik sa nakaraang tanong'}
                >
                  <span aria-hidden>←</span>
                  {showEnglish ? 'Previous' : 'Nakaraan'}
                </Button>

                <div className="flex flex-col items-center gap-1 px-0 sm:px-2">
                  <div
                    className="rounded-lg bg-slate-100/80 px-4 py-2 text-sm font-medium text-slate-600"
                    role="status"
                    aria-live="polite"
                  >
                    {showEnglish
                      ? `Question ${currentQuestionIndex + 1} of ${totalQuestions}`
                      : `Tanong ${currentQuestionIndex + 1} ng ${totalQuestions}`}
                  </div>
                  {isSavingResponse && (
                    <span className="animate-pulse text-xs text-primary">
                      {showEnglish ? 'Saving...' : 'Sine-save...'}
                    </span>
                  )}
                  {!isSavingResponse && isAnswered && (
                    <span className="text-xs font-medium text-emerald-600">
                      {showEnglish ? 'Saved' : 'Nai-save na'}
                    </span>
                  )}
                  {!hasAssessmentSession && (
                    <span className="animate-pulse text-xs text-amber-600">
                      {showEnglish ? 'Preparing session...' : 'Inihahanda ang session...'}
                    </span>
                  )}
                </div>

                {isLastQuestion ? (
                  <Button
                    onClick={() => handleSubmitAssessment(false)}
                    disabled={!canProceed || isSubmitting || isSavingResponse || !hasAssessmentSession}
                    variant="default"
                    className="w-full gap-2 disabled:opacity-50 sm:w-auto"
                    aria-label={showEnglish ? 'Submit assessment' : 'I-submit ang assessment'}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        <span>{showEnglish ? 'Analyzing...' : 'Sinusuri...'}</span>
                      </>
                    ) : (
                      <>
                        <span>
                          {showEnglish ? 'Submit & Get Results' : 'I-submit at tingnan ang resulta'}
                        </span>
                        <Check className="h-4 w-4" aria-hidden />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => void handleNextQuestion()}
                    disabled={!canProceed || isSavingResponse || isSubmitting || !hasAssessmentSession}
                    variant="default"
                    className="w-full gap-2 disabled:opacity-50 sm:w-auto"
                    aria-label={showEnglish ? 'Go to next question' : 'Pumunta sa susunod na tanong'}
                  >
                    {showEnglish ? 'Next' : 'Susunod'}
                    <span aria-hidden>→</span>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Help Card */}
          <Card className="border-transparent bg-gradient-to-r from-teal-50 to-cyan-50 shadow-sm dark:border dark:border-border dark:bg-gradient-to-r dark:from-card dark:to-muted/50">
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center text-2xl">
                  💡
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold text-foreground">
                    {showEnglish ? 'Tips for answering' : 'Mga tip sa pagsagot'}
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">
                    {showEnglish ? (
                      <>
                        <li>Rate based on your child&apos;s typical behavior, not only their best day</li>
                        <li>For scale questions use 1–5; for multiple choice or yes/no, pick what best matches what you see</li>
                        <li>
                          Answers roll up into four areas (speech, understanding, expression, communication flow) plus age
                          for the screening estimate
                        </li>
                      </>
                    ) : (
                      <>
                        <li>Mag-rate batay sa karaniwang kilos ng anak, hindi lang sa pinakamabuting araw</li>
                        <li>Sa scale tanong gamitin ang 1–5; sa multiple choice o oo/hindi, piliin ang pinakamalapit sa napapansin mo</li>
                        <li>
                          Ang mga sagot ay tinipon sa apat na bahagi (pagsasalita, pag-unawa, ekspresyon, daloy ng komunikasyon)
                          kasama ang edad para sa screening estimate
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  )
}
